<template>
  <div class="localizacao-input" ref="containerEl">
    <FormField
      label="Localização"
      id="local"
      required
      helpText="Informe o endereço ou localização da atividade (esta informação será gravada)"
    >
      <input
        type="text"
        id="local"
        class="field-input"
        :value="localTexto"
        @input="onLocalInput"
        maxlength="256"
        placeholder="Ex: Rua Felipe Santiago, 411 - Russas/CE ou Ambiente Virtual"
        required
      />
    </FormField>

    <FormField
      label="Buscar local no mapa (Opcional)"
      id="busca-mapa"
      helpText="Use esta caixa de texto apenas para localizar o endereço no mapa ou clique em 'Ver no mapa'"
    >
      <!-- Seção da caixa de busca do mapa e dropdown flutuante -->
      <div class="search-input-section">
        <div class="input-controls-wrapper">
          <div
            class="search-input-box"
            :class="{ 'is-focused': isInputFocused, 'has-value': !!buscaMapa }"
          >
            <i class="fa-solid fa-magnifying-glass leading-icon" aria-hidden="true"></i>

            <input
              type="text"
              id="busca-mapa"
              class="styled-input"
              v-model="buscaMapa"
              @input="debouncePesquisa"
              @focus="isInputFocused = true"
              @blur="handleBlur"
              @keydown.enter.prevent="buscarLocais"
              placeholder="Pesquise endereço ou local para marcar no mapa..."
              autocomplete="off"
            />

            <div class="input-actions">
              <span v-if="isLoading" class="spinner-icon" title="Buscando...">
                <i class="fa-solid fa-circle-notch fa-spin"></i>
              </span>

              <button
                v-if="buscaMapa && !isLoading"
                type="button"
                class="btn-icon btn-clear"
                @click="limparBuscaMapa"
                title="Limpar busca do mapa"
              >
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>

          <button
            type="button"
            class="btn-toggle-mapa"
            :class="{ 'is-active': mostrarMapa }"
            @click="alternarMapa"
          >
            <i
              class="fa-solid"
              :class="mostrarMapa ? 'fa-map-location' : 'fa-map-location-dot'"
            ></i>
            <span>{{ mostrarMapa ? "Ocultar mapa" : "Ver no mapa" }}</span>
          </button>
        </div>

        <div v-if="pontoSelecionado" class="coordenadas-chip">
          <span class="coordenadas-texto">
            <i class="fa-solid fa-location-dot"></i>
            Ponto marcado no mapa ({{ Number(pontoSelecionado.latitude).toFixed(5) }},
            {{ Number(pontoSelecionado.longitude).toFixed(5) }})
          </span>
          <button
            type="button"
            class="btn-remover-marcacao"
            @click="limparMarcacaoMapa"
          >
            Remover marcação
          </button>
        </div>

        <!-- Dropdown flutuante de sugestões de busca do mapa -->
        <transition name="dropdown-fade">
          <div
            v-if="mostrarDropdown"
            class="dropdown-resultados"
          >
            <div v-if="isLoading && (!resultados || !resultados.length)" class="dropdown-status">
              <i class="fa-solid fa-circle-notch fa-spin status-icon"></i>
              <span>Buscando locais correspondentes no mapa...</span>
            </div>

            <div v-else-if="resultados && resultados.length" class="dropdown-list">
              <div class="dropdown-header">
                <i class="fa-solid fa-magnifying-glass"></i>
                <span>Locais encontrados no mapa</span>
              </div>
              <div
                v-for="(localItem, idx) in resultados"
                :key="idx"
                class="resultado-item"
                @mousedown.prevent="selecionarLocal(localItem)"
              >
                <div class="pin-wrapper">
                  <i class="fa-solid fa-location-dot"></i>
                </div>
                <div class="local-textos">
                  <span class="local-nome">{{ localItem.name || localItem.display_name.split(",")[0] }}</span>
                  <span class="local-detalhes">{{ localItem.display_name }}</span>
                </div>
              </div>
            </div>

            <div v-else-if="!isLoading && buscaMapa.trim().length >= 3 && resultados && resultados.length === 0" class="dropdown-status empty">
              <i class="fa-regular fa-face-frown status-icon"></i>
              <span>Nenhum ponto encontrado no mapa (o texto em Localização continuará salvo normalmente).</span>
            </div>
          </div>
        </transition>
      </div>

      <!-- Container do Mapa -->
      <transition name="mapa-slide">
        <div v-if="mostrarMapa" class="mapa-wrapper">
          <div class="mapa-header">
            <div class="mapa-header-title">
              <i class="fa-solid fa-hand-pointer"></i>
              <span>Clique em qualquer ponto do mapa para marcar a localização exata</span>
            </div>
            <button
              type="button"
              class="btn-fechar-mapa"
              @click="mostrarMapa = false"
              title="Fechar mapa"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
          <MapaLeaflet
            ref="mapaRef"
            selecionavel
            :marcadores="marcadores"
            :centro="centroInicial"
            :zoom="pontoSelecionado ? 16 : 13"
            :ajustar-aos-marcadores="false"
            @selecionar="selecionarPeloMapa"
          />
        </div>
      </transition>
    </FormField>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import FormField from "./FormField.vue";
import MapaLeaflet from "./MapaLeaflet.vue";

const emit = defineEmits(["localizacaoSelecionada", "update:local"]);

const props = defineProps({
  latitude: [Number, String],
  longitude: [Number, String],
  local: {
    type: String,
    default: "",
  },
});

const containerEl = ref(null);
const localTexto = ref(props.local || "");
const buscaMapa = ref("");
const isLoading = ref(false);
const resultados = ref(null);
const isInputFocused = ref(false);

let timeoutId = null;
let coordenadasAtuais = { lat: null, lon: null };

// ---- Mapa ----
const COORD_PADRAO = [-3.7319, -38.5267]; // Fortaleza
const mostrarMapa = ref(false);
const mapaRef = ref(null);
const pontoSelecionado = ref(null);

const marcadores = computed(() =>
  pontoSelecionado.value ? [pontoSelecionado.value] : []
);

const centroInicial = computed(() =>
  pontoSelecionado.value
    ? [
        Number(pontoSelecionado.value.latitude),
        Number(pontoSelecionado.value.longitude),
      ]
    : COORD_PADRAO
);

function onLocalInput(event) {
  localTexto.value = event.target.value;
  emit("update:local", localTexto.value);
}

function definirPonto(latitude, longitude, centralizar = true) {
  pontoSelecionado.value = {
    latitude: Number(latitude),
    longitude: Number(longitude),
  };
  if (centralizar) {
    nextTick(() => {
      mapaRef.value?.centralizar(latitude, longitude);
    });
  }
}

const mostrarDropdown = computed(() => {
  return (
    isInputFocused.value &&
    (isLoading.value ||
      (resultados.value && resultados.value.length > 0) ||
      (!isLoading.value &&
        buscaMapa.value.trim().length >= 3 &&
        Array.isArray(resultados.value) &&
        resultados.value.length === 0))
  );
});

function handleBlur() {
  setTimeout(() => {
    isInputFocused.value = false;
  }, 200);
}

function limparBuscaMapa() {
  buscaMapa.value = "";
  resultados.value = null;
}

function limparMarcacaoMapa() {
  buscaMapa.value = "";
  resultados.value = null;
  coordenadasAtuais = { lat: null, lon: null };
  pontoSelecionado.value = null;
  emit("localizacaoSelecionada", {
    latitude: null,
    longitude: null,
    nome: localTexto.value,
  });
}

const debouncePesquisa = () => {
  if (!buscaMapa.value.trim()) {
    isLoading.value = false;
    resultados.value = null;
    return;
  }
  isLoading.value = true;
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
  timeoutId = setTimeout(() => {
    buscarLocais();
  }, 600);
};

const buscarLocais = async () => {
  if (!buscaMapa.value.trim()) {
    isLoading.value = false;
    resultados.value = null;
    return;
  }

  isLoading.value = true;
  resultados.value = null;

  try {
    const query = encodeURIComponent(buscaMapa.value.trim());
    const urlBusca = `https://nominatim.openstreetmap.org/search?q=${query}&format=jsonv2`;

    const response = await fetch(urlBusca);
    const json = await response.json();
    resultados.value = Array.isArray(json) ? [...json] : [];
  } catch (error) {
    console.error("Ocorreu um erro ao buscar locais no mapa:", error);
    resultados.value = [];
  } finally {
    isLoading.value = false;
  }
};

const buscarLocalPorCoordenadas = async (lat, lon) => {
  isLoading.value = true;
  try {
    const urlReversa = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=jsonv2`;
    const response = await fetch(urlReversa);
    const data = await response.json();
    if (data) {
      buscaMapa.value = data.name || data.display_name || "";
    }
    return buscaMapa.value;
  } catch (error) {
    console.error("Erro ao buscar local por coordenadas:", error);
    return "";
  } finally {
    isLoading.value = false;
  }
};

async function selecionarPeloMapa({ latitude, longitude }) {
  coordenadasAtuais = { lat: latitude, lon: longitude };
  definirPonto(latitude, longitude, false);
  resultados.value = null;
  await buscarLocalPorCoordenadas(latitude, longitude);
  emit("localizacaoSelecionada", {
    latitude,
    longitude,
    nome: localTexto.value,
  });
}

function alternarMapa() {
  mostrarMapa.value = !mostrarMapa.value;
}

function selecionarLocal(localItem) {
  resultados.value = null;
  isInputFocused.value = false;
  buscaMapa.value = localItem.name || localItem.display_name.split(",")[0];
  coordenadasAtuais = {
    lat: Number(localItem.lat),
    lon: Number(localItem.lon),
  };
  mostrarMapa.value = true;
  definirPonto(localItem.lat, localItem.lon, true);
  emit("localizacaoSelecionada", {
    latitude: Number(localItem.lat),
    longitude: Number(localItem.lon),
    nome: localTexto.value,
  });
}

watch(
  [() => props.latitude, () => props.longitude],
  ([newLat, newLon]) => {
    const temCoord =
      newLat !== null &&
      newLat !== undefined &&
      newLat !== "" &&
      newLon !== null &&
      newLon !== undefined &&
      newLon !== "";

    if (!temCoord) {
      coordenadasAtuais = { lat: null, lon: null };
      pontoSelecionado.value = null;
      return;
    }

    if (
      coordenadasAtuais.lat !== null &&
      Number(newLat) === Number(coordenadasAtuais.lat) &&
      Number(newLon) === Number(coordenadasAtuais.lon)
    ) {
      return;
    }

    coordenadasAtuais = { lat: newLat, lon: newLon };
    definirPonto(newLat, newLon);
    buscarLocalPorCoordenadas(newLat, newLon);
  },
  { immediate: true }
);

watch(
  () => props.local,
  (novoLocal) => {
    const valorNormalizado = novoLocal ?? "";
    if (valorNormalizado !== localTexto.value) {
      localTexto.value = valorNormalizado;
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.localizacao-input {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-input-section {
  position: relative;
  width: 100%;
  z-index: 100;
}

.input-controls-wrapper {
  display: flex;
  gap: 8px;
  align-items: stretch;
  width: 100%;
}

.search-input-box {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  background-color: var(--branco, #ffffff);
  border: 1.5px solid var(--cinza-claro, #e2e8f0);
  border-radius: 8px;
  padding: 0 12px;
  min-height: 44px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.06);
}

.search-input-box:hover {
  border-color: var(--roxo-claro, #9061f9);
}

.search-input-box.is-focused {
  border-color: var(--roxo-medio, #6c2bd9);
  background-color: var(--branco, #ffffff);
  box-shadow: 0 0 0 3px hsla(258, 46%, 46%, 0.15);
}

.leading-icon {
  color: var(--roxo-medio, #6c2bd9);
  font-size: 14px;
  margin-right: 8px;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-input-box.is-focused .leading-icon {
  transform: scale(1.08);
}

.styled-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  font-family: inherit;
  color: var(--cinza-escuro, #2d3748);
  width: 100%;
  padding: 8px 0;
}

.styled-input::placeholder {
  color: var(--cinza-medio, #a0aec0);
  font-size: 14px;
}

.input-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
}

.spinner-icon {
  color: var(--roxo-medio, #6c2bd9);
  font-size: 14px;
  display: flex;
  align-items: center;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  color: var(--cinza-medio, #a0aec0);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-icon:hover {
  color: var(--vermelho, #e53e3e);
  background-color: var(--roxo-superficie, #f7f5ff);
}

.btn-toggle-mapa {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  border: 1.5px solid var(--roxo-superficie, #ece7fe);
  border-radius: 8px;
  background-color: var(--roxo-superficie, #f7f5ff);
  color: var(--roxo-primario, #371870);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-toggle-mapa i {
  font-size: 16px;
  color: var(--roxo-medio, #6c2bd9);
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-toggle-mapa:hover {
  background-color: var(--roxo-medio, #6c2bd9);
  border-color: var(--roxo-medio, #6c2bd9);
  color: var(--branco, #ffffff);
  box-shadow: 0 4px 12px hsla(258, 46%, 46%, 0.25);
  transform: translateY(-1px);
}

.btn-toggle-mapa:hover i {
  color: var(--branco, #ffffff);
  transform: scale(1.08);
}

.btn-toggle-mapa.is-active {
  background-color: var(--roxo-primario, #371870);
  border-color: var(--roxo-primario, #371870);
  color: var(--branco, #ffffff);
}

.btn-toggle-mapa.is-active i {
  color: var(--branco, #ffffff);
}

.coordenadas-chip {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: var(--roxo-superficie, #f7f5ff);
  border: 1px solid hsla(258, 46%, 46%, 0.2);
  font-size: 12px;
}

.coordenadas-texto {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--roxo-primario, #371870);
  font-weight: 600;
}

.btn-remover-marcacao {
  background: transparent;
  border: none;
  color: var(--vermelho, #e53e3e);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-remover-marcacao:hover {
  background-color: var(--vermelho-claro, #fee2e2);
}

.dropdown-resultados {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: var(--branco, #ffffff);
  border: 1px solid hsla(258, 46%, 46%, 0.16);
  border-radius: 12px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  max-height: 250px;
  overflow-y: auto;
  z-index: 1300;
}

.dropdown-resultados::-webkit-scrollbar {
  width: 6px;
}
.dropdown-resultados::-webkit-scrollbar-track {
  background: transparent;
}
.dropdown-resultados::-webkit-scrollbar-thumb {
  background: var(--cinza-claro, #e2e8f0);
  border-radius: 8px;
}
.dropdown-resultados::-webkit-scrollbar-thumb:hover {
  background: var(--roxo-claro, #9061f9);
}

.dropdown-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--roxo-medio, #6c2bd9);
  background: var(--roxo-superficie, #faf8ff);
  border-bottom: 1px solid hsla(258, 46%, 46%, 0.08);
}

.dropdown-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  font-size: 14px;
  color: var(--cinza-escuro, #4a5568);
  justify-content: center;
}

.dropdown-status.empty {
  color: var(--cinza-medio, #718096);
}

.status-icon {
  font-size: 16px;
  color: var(--roxo-medio, #6c2bd9);
}

.dropdown-list {
  display: flex;
  flex-direction: column;
}

.resultado-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid var(--cinza-claro, #f1f5f9);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.resultado-item:last-child {
  border-bottom: none;
}

.resultado-item:hover {
  background-color: var(--roxo-superficie, #f7f5ff);
}

.pin-wrapper {
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background-color: var(--roxo-superficie, #f3f0ff);
  color: var(--roxo-medio, #6c2bd9);
  font-size: 14px;
  flex-shrink: 0;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.resultado-item:hover .pin-wrapper {
  background-color: var(--roxo-medio, #6c2bd9);
  color: var(--branco, #ffffff);
}

.local-textos {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.local-nome {
  font-size: 14px;
  font-weight: 600;
  color: var(--roxo-escuro, #1a0933);
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.local-detalhes {
  font-size: 12px;
  color: var(--cinza-medio, #64748b);
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mapa-wrapper {
  position: relative;
  z-index: 1;
  margin-top: 12px;
  border-radius: 12px;
  overflow: hidden;
  border: 1.5px solid hsla(258, 46%, 46%, 0.18);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  background: var(--branco, #ffffff);
}

.mapa-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: linear-gradient(135deg, var(--roxo-superficie, #f7f5ff) 0%, #ffffff 100%);
  border-bottom: 1px solid hsla(258, 46%, 46%, 0.1);
}

.mapa-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--roxo-primario, #371870);
}

.mapa-header-title i {
  color: var(--roxo-medio, #6c2bd9);
}

.btn-fechar-mapa {
  background: none;
  border: none;
  color: var(--cinza-medio, #718096);
  cursor: pointer;
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-fechar-mapa:hover {
  background-color: var(--vermelho-claro, #fee2e2);
  color: var(--vermelho, #ef4444);
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.mapa-slide-enter-active,
.mapa-slide-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.mapa-slide-enter-from,
.mapa-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@media (max-width: 576px) {
  .input-controls-wrapper {
    flex-direction: column;
  }

  .btn-toggle-mapa {
    justify-content: center;
    padding: 8px 16px;
  }
}
</style>
